import { useRef, useState } from 'react';
import { Shield } from 'lucide-react';
import html2canvas from 'html2canvas';

const LOGO_WORDMARK = '/myronko-wordmark.svg';
const EMBER = '#f26b3b';

// Short, generic taglines — describe the achievement itself, never the protocol behind it
// (no compound names, doses, or sites), so the card stays safe to share publicly.
const STREAK_CAPTIONS = {
  7: 'One week of showing up.',
  14: 'Two weeks strong.',
  30: 'A full month, one day at a time.',
  60: 'Two months in the books.',
  90: 'A quarter-year of consistency.',
  180: 'Half a year strong.',
  365: 'One full year. Own it.',
};
const SHOTS_CAPTIONS = {
  1: 'First dose logged.',
  10: 'Ten doses down.',
  25: 'Twenty-five and counting.',
  50: 'Halfway to a hundred.',
  100: 'Triple digits.',
  250: 'Deep in the protocol.',
  500: 'Five hundred doses strong.',
};

export function MilestoneCard({ milestone, streak, onClose, onToast, dm = true }) {
  const isStreak = milestone.type === 'streak';
  const value = milestone.value;
  const caption = (isStreak ? STREAK_CAPTIONS : SHOTS_CAPTIONS)[value];

  const card = (
    <div id="mr-milestone-card" style={{ width: 400, height: 400, background: '#080c10', borderRadius: 16, padding: 32, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', border: `1px solid ${EMBER}33`, position: 'relative', overflow: 'hidden', fontFamily: "Georgia,'Times New Roman',serif" }}>
      <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(ellipse at 80% 20%,${EMBER}18 0%,transparent 60%)` }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative' }}>
        <img src={LOGO_WORDMARK} alt="MyRONKO" style={{ height: 18, width: 'auto' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 20, background: `${EMBER}22`, border: `1px solid ${EMBER}44` }}>
          <Shield size={11} style={{ color: EMBER }} />
          <span style={{ fontSize: 9, fontFamily: 'monospace', color: EMBER, letterSpacing: '0.1em' }}>MILESTONE</span>
        </div>
      </div>
      <div style={{ textAlign: 'center', position: 'relative', padding: '0 16px' }}>
        <div style={{ fontSize: 72, fontWeight: 'bold', color: EMBER, lineHeight: 1, letterSpacing: '-0.02em' }}>{value}{isStreak ? '' : '+'}</div>
        <div style={{ fontSize: 14, color: '#c8dde8', marginTop: 6, letterSpacing: '0.08em' }}>{isStreak ? 'DAY STREAK' : 'SHOTS LOGGED'}</div>
        {caption && <div style={{ fontSize: 12, color: 'rgba(200,221,232,0.5)', marginTop: 14, lineHeight: 1.5 }}>{caption}</div>}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', position: 'relative' }}>
        <div style={{ fontSize: 9, fontFamily: 'monospace', color: 'rgba(0,216,240,0.4)', letterSpacing: '0.15em' }}>OWN YOUR PROTOCOL.</div>
        <div style={{ fontSize: 9, fontFamily: 'monospace', color: 'rgba(255,255,255,0.2)' }}>{new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).toUpperCase()}</div>
      </div>
    </div>
  );

  const [busy, setBusy] = useState(false);
  // React's state commit isn't synchronous with the click that triggered it -- a fast enough
  // second click can still read the stale `busy` closure before the re-render lands. A ref
  // updates immediately and is read synchronously on the very next call, closing that gap;
  // `busy` (state) still exists purely to drive the disabled/"…" UI feedback.
  const busyRef = useRef(false);

  // Both actions render the same expensive (~1s) html2canvas capture -- guard against a
  // rapid double-tap firing two concurrent renders and producing duplicate downloads/share sheets.
  const handleSave = () => {
    if (busyRef.current) return;
    const el = document.getElementById('mr-milestone-card');
    if (!el) return;
    busyRef.current = true;
    setBusy(true);
    html2canvas(el, { scale: 2, backgroundColor: null }).then(canvas => {
      const uri = canvas.toDataURL('image/png');
      const a = document.createElement('a'); a.href = uri;
      a.download = `myronko-milestone-${value}${isStreak ? 'day-streak' : 'shots'}.png`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
    }).catch(() => onToast?.('Could not save image — try again')).finally(() => { busyRef.current = false; setBusy(false); });
  };

  const handleShare = () => {
    if (busyRef.current) return;
    const text = `${value}${isStreak ? ' day streak' : ' shots logged'} on MyRONKO. Own Your Protocol. 🛡️ myronko.app`;
    const el = document.getElementById('mr-milestone-card');
    if (!el) return;
    busyRef.current = true;
    setBusy(true);
    html2canvas(el, { scale: 2, backgroundColor: null })
      .then(canvas => new Promise(resolve => canvas.toBlob(resolve, 'image/png')))
      .then(blob => {
        if (!blob) { onToast?.('Could not share image — try again'); return; }
        const file = new File([blob], `myronko-milestone-${value}${isStreak ? 'day-streak' : 'shots'}.png`, { type: 'image/png' });
        if (navigator.canShare?.({ files: [file] })) {
          // AbortError just means the user closed the native share sheet -- expected, stay
          // silent. Anything else (permission denial, share-target size limits, etc.) should
          // actually tell the user, unlike before where every failure here was swallowed.
          return navigator.share({ title: 'MyRONKO Milestone', text, files: [file] }).catch(err => {
            if (err?.name !== 'AbortError') onToast?.('Could not share image — try again');
          });
        } else if (navigator.share) {
          onToast?.('Your device can\'t share the image directly — save it first, then attach it manually');
          return navigator.share({ title: 'MyRONKO Milestone', text }).catch(() => {});
        } else {
          return navigator.clipboard?.writeText(text).then(() => onToast?.('Caption copied — save the card and post!'));
        }
      })
      .catch(() => onToast?.('Could not share image — try again'))
      .finally(() => { busyRef.current = false; setBusy(false); });
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 200, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ width: '100%', maxWidth: 480, background: dm ? '#080c10' : '#f4f8fc', borderRadius: '20px 20px 0 0', padding: 24, animation: 'sf 0.3s ease', borderTop: '2px solid rgba(242,107,59,0.4)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 9, fontFamily: 'monospace', color: 'rgba(242,107,59,0.6)', letterSpacing: '0.15em', marginBottom: 3 }}>MILESTONE UNLOCKED</div>
            <div style={{ fontSize: 18, color: dm ? '#c8dde8' : '#1a2332', fontWeight: '600' }}>{isStreak ? `${value} Day Streak` : `${value} Shots Logged`}</div>
          </div>
          <button data-testid="milestone-close-btn" onClick={onClose} style={{ background: 'none', border: 'none', color: dm ? '#3a5060' : '#6a8090', cursor: 'pointer', fontSize: 22, lineHeight: 1 }}>×</button>
        </div>
        <div style={{ overflowX: 'auto', marginBottom: 16, borderRadius: 12 }}>
          <div style={{ display: 'inline-block' }}>{card}</div>
        </div>
        <div style={{ display: 'flex', gap: 9 }}>
          <button onClick={handleSave} disabled={busy} style={{ flex: 1, padding: '12px', background: 'rgba(242,107,59,0.12)', color: '#f26b3b', fontSize: 13, fontWeight: '700', border: '1px solid rgba(242,107,59,0.4)', borderRadius: 12, cursor: busy ? 'default' : 'pointer', fontFamily: 'inherit', opacity: busy ? 0.6 : 1 }}>{busy ? '…' : '⬇ Save Image'}</button>
          <button onClick={handleShare} disabled={busy} style={{ flex: 1, padding: '12px', background: dm ? 'rgba(0,216,240,0.08)' : 'rgba(0,112,168,0.08)', color: dm ? '#00d8f0' : '#0070a8', fontSize: 13, fontWeight: '700', border: `1px solid ${dm ? 'rgba(0,216,240,0.3)' : 'rgba(0,112,168,0.3)'}`, borderRadius: 12, cursor: busy ? 'default' : 'pointer', fontFamily: 'inherit', opacity: busy ? 0.6 : 1 }}>{busy ? '…' : '↗ Share'}</button>
        </div>
        <div style={{ marginTop: 10, fontSize: 10, color: dm ? '#2a4050' : '#6a8090', fontFamily: 'monospace', textAlign: 'center' }}>Tap Save Image to download</div>
      </div>
    </div>
  );
}
